/**
 * Ægis App UI
 *
 * Manages all screens and their interactions with the pipeline.
 * Anti-surveillance aesthetics: no meters, no scores, no red/green.
 *
 * Screen flow:
 *   home → activities (Seedling/Blossom) → activity player
 *   home → lesson (Kindling)             → lesson player
 */

import type { LearningConditionState } from '@aegis/inference';
import type { Intervention } from '@aegis/policy';
import { PerceptionTier } from '@aegis/feature-stream';
import type { RoomPresence } from '@aegis/feature-stream';
import type { PerceptionState } from '../perception/web-perception';
import type { AegisPipeline } from '../pipeline/aegis-pipeline';
import { LESSONS } from '../content/lessons';
import type { Lesson, LessonSection } from '../content/lessons';
import { LEVELS, type DevelopmentalLevel } from '../content/developmental-levels';
import { TODDLER_ACTIVITIES, type ToddlerActivity } from '../content/toddler-activities';
import { renderActivity, type CleanupFn } from './toddler-ui';
import { SamwiseCompanion } from '../samwise/samwise-companion';
import type { ConversationPrompt } from '../samwise/samwise-persona';

type Screen = 'home' | 'activities' | 'activity' | 'lesson' | 'privacy';

export class AppUI {
  private pipeline: AegisPipeline;
  private sam: SamwiseCompanion;
  private currentScreen: Screen = 'home';
  private currentLevel: DevelopmentalLevel | null = null;
  private currentLesson: Lesson | null = null;
  private currentSectionIndex = 0;
  private drawerOpen = false;
  private currentIntervention: Intervention | null = null;
  private activityCleanup: CleanupFn | null = null;

  // SAMWISE DOM refs
  private samDialogue: HTMLElement;
  private samText: HTMLElement;
  private samChoices: HTMLElement;

  // DOM refs
  private app: HTMLElement;
  private homeScreen: HTMLElement;
  private activitiesScreen: HTMLElement;
  private activityScreen: HTMLElement;
  private lessonScreen: HTMLElement;
  private privacyScreen: HTMLElement;
  private lessonContent: HTMLElement;
  private pulseIndicator: HTMLElement;
  private interventionOverlay: HTMLElement;
  private explainabilityDrawer: HTMLElement;
  private cameraDialog: HTMLElement;
  private microBreak: HTMLElement;
  private statusBar: HTMLElement;

  constructor(pipeline: AegisPipeline) {
    this.pipeline = pipeline;
    this.app = document.getElementById('app')!;

    // Screen refs
    this.homeScreen = document.getElementById('screen-home')!;
    this.activitiesScreen = document.getElementById('screen-activities')!;
    this.activityScreen = document.getElementById('screen-activity')!;
    this.lessonScreen = document.getElementById('screen-lesson')!;
    this.privacyScreen = document.getElementById('screen-privacy')!;
    this.lessonContent = document.getElementById('lesson-content')!;
    this.pulseIndicator = document.getElementById('pulse')!;
    this.interventionOverlay = document.getElementById('intervention-overlay')!;
    this.explainabilityDrawer = document.getElementById('explainability-drawer')!;
    this.cameraDialog = document.getElementById('camera-dialog')!;
    this.microBreak = document.getElementById('micro-break')!;
    this.statusBar = document.getElementById('status-bar')!;

    // SAMWISE DOM refs
    this.samDialogue = document.getElementById('samwise-dialogue')!;
    this.samText = document.getElementById('samwise-text')!;
    this.samChoices = document.getElementById('samwise-choices')!;

    // Initialize SAMWISE companion
    this.sam = new SamwiseCompanion({
      onSpeakingChange: (speaking) => {
        this.samDialogue.classList.toggle('speaking', speaking);
      },
      onDialogue: (text) => {
        this.showSamDialogue(text);
      },
      onChoices: (prompt) => {
        this.showSamChoices(prompt);
      },
      onDialogueClear: () => {
        this.hideSamDialogue();
      },
    });

    this.renderLevelGrid();
    this.bindEvents();
  }

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------

  showScreen(screen: Screen): void {
    // Cleanup any running activity
    if (this.currentScreen === 'activity' && screen !== 'activity') {
      this.cleanupActivity();
    }

    // If returning to home from a level, Sam says farewell
    if (screen === 'home' && this.currentScreen !== 'home') {
      this.sam.farewell();
    }

    this.currentScreen = screen;
    document.querySelectorAll('.screen').forEach(s => s.classList.remove('active'));

    switch (screen) {
      case 'home':
        this.homeScreen.classList.add('active');
        this.pipeline.pause();
        break;
      case 'activities':
        this.activitiesScreen.classList.add('active');
        break;
      case 'activity':
        this.activityScreen.classList.add('active');
        break;
      case 'lesson':
        this.lessonScreen.classList.add('active');
        break;
      case 'privacy':
        this.privacyScreen.classList.add('active');
        break;
    }
  }

  // -------------------------------------------------------------------------
  // Home screen: level grid
  // -------------------------------------------------------------------------

  private renderLevelGrid(): void {
    const grid = document.getElementById('level-grid')!;
    grid.innerHTML = LEVELS.map(level => `
      <button
        class="level-card level-card--${level.id} ${level.available ? '' : 'level-card--unavailable'}"
        data-level-id="${level.id}"
        ${level.available ? '' : 'disabled'}
      >
        ${level.available ? '' : '<span class="level-card__badge">coming soon</span>'}
        <div class="level-card__name">${level.name}</div>
        <div class="level-card__ages">ages ${level.ages}</div>
        <div class="level-card__motto">${level.motto}</div>
        <div class="level-card__subtitle">${level.subtitle}</div>
      </button>
    `).join('');

    // Bind level card clicks
    grid.querySelectorAll<HTMLElement>('.level-card:not(.level-card--unavailable)').forEach(card => {
      card.addEventListener('click', () => {
        const levelId = card.dataset.levelId!;
        this.selectLevel(levelId);
      });
    });
  }

  private selectLevel(levelId: string): void {
    const level = LEVELS.find(l => l.id === levelId);
    if (!level || !level.available) return;

    this.currentLevel = level;

    // SAMWISE greets the student for this level
    this.sam.greetForLevel(levelId);

    // Apply toddler styling for seedling/blossom levels
    if (levelId === 'seedling' || levelId === 'blossom') {
      this.samDialogue.classList.add('samwise-dialogue--toddler');
    } else {
      this.samDialogue.classList.remove('samwise-dialogue--toddler');
    }

    if (level.id === 'kindling') {
      // Older students get the philosophical lesson
      this.startLesson();
    } else if (level.id === 'seedling') {
      // Toddlers get the activity picker
      this.showActivities(level);
    }
  }

  // -------------------------------------------------------------------------
  // Activity picker (Seedling level)
  // -------------------------------------------------------------------------

  private showActivities(level: DevelopmentalLevel): void {
    document.getElementById('activities-level-name')!.textContent = level.name;
    document.getElementById('activities-level-motto')!.textContent = level.motto;

    const grid = document.getElementById('activity-grid')!;
    grid.innerHTML = TODDLER_ACTIVITIES.map(act => `
      <button class="activity-card" data-activity-id="${act.id}">
        <div class="activity-card__icon">${act.icon}</div>
        <div class="activity-card__name">${act.name}</div>
        <div class="activity-card__desc">${act.description}</div>
      </button>
    `).join('');

    // Bind activity clicks
    grid.querySelectorAll<HTMLElement>('.activity-card').forEach(card => {
      card.addEventListener('click', () => {
        const actId = card.dataset.activityId!;
        this.startActivity(actId);
      });
    });

    this.showScreen('activities');
  }

  // -------------------------------------------------------------------------
  // Activity player
  // -------------------------------------------------------------------------

  private startActivity(activityId: string): void {
    const activity = TODDLER_ACTIVITIES.find(a => a.id === activityId);
    if (!activity) return;

    document.getElementById('activity-title')!.textContent = activity.name;
    const container = document.getElementById('activity-container')!;

    this.showScreen('activity');

    // Render after screen transition
    requestAnimationFrame(() => {
      this.activityCleanup = renderActivity(container, activity);
    });
  }

  private cleanupActivity(): void {
    if (this.activityCleanup) {
      this.activityCleanup();
      this.activityCleanup = null;
    }
    const container = document.getElementById('activity-container');
    if (container) container.innerHTML = '';
  }

  // -------------------------------------------------------------------------
  // Lesson flow (for older levels)
  // -------------------------------------------------------------------------

  async startLesson(lessonId?: string): Promise<void> {
    const lesson = LESSONS.find(l => l.id === lessonId) ?? LESSONS[0];
    this.currentLesson = lesson;
    this.currentSectionIndex = 0;

    this.showScreen('lesson');
    document.getElementById('lesson-title')!.textContent = lesson.title;
    this.renderLesson();

    const scroller = document.getElementById('lesson-scroller')!;
    this.pipeline.attachTracking(scroller);

    const perceptionState = this.pipeline.getPerceptionState();
    if (perceptionState === 'uninitialized') {
      this.showCameraDialog();
    } else {
      this.pipeline.start();
    }
  }

  // -------------------------------------------------------------------------
  // Lesson rendering
  // -------------------------------------------------------------------------

  private renderLesson(): void {
    if (!this.currentLesson) return;

    const html = this.currentLesson.sections
      .map(section => this.renderSection(section))
      .join('');

    this.lessonContent.innerHTML = `<div class="lesson-content">${html}</div>`;
    this.bindLessonEvents();
  }

  private renderSection(section: LessonSection): string {
    switch (section.type) {
      case 'text':
        return `<div id="section-${section.id}" class="lesson-section">${section.content}</div>`;

      case 'quote':
        return `<div id="section-${section.id}" class="lesson-section">
          <blockquote>${section.content}</blockquote>
        </div>`;

      case 'reflection':
        return `<div id="section-${section.id}" class="lesson-section">
          <div class="reflection">
            <div class="reflection__prompt">${section.prompt}</div>
            <textarea class="reflection__input" placeholder="Take your time..." rows="4"></textarea>
          </div>
        </div>`;

      case 'exercise':
        return `<div id="section-${section.id}" class="lesson-section">
          <div class="exercise" data-section-id="${section.id}">
            <p class="exercise__question">${section.content}</p>
            <div class="exercise__options">
              ${section.options!.map(opt => `
                <button class="exercise__option" data-option-id="${opt.id}" data-feedback="${this.escapeAttr(opt.feedback)}">
                  ${opt.text}
                </button>
              `).join('')}
            </div>
            <div class="exercise__feedback" style="display: none;"></div>
          </div>
        </div>`;

      default:
        return '';
    }
  }

  private bindLessonEvents(): void {
    this.lessonContent.querySelectorAll('.exercise__option').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const exercise = target.closest('.exercise')!;
        const feedback = target.dataset.feedback!;
        const feedbackEl = exercise.querySelector('.exercise__feedback') as HTMLElement;

        exercise.querySelectorAll('.exercise__option').forEach(o => o.classList.remove('selected'));
        target.classList.add('selected');

        feedbackEl.textContent = feedback;
        feedbackEl.style.display = 'block';

        this.pipeline.setContentElement(exercise.dataset.sectionId ?? '');
      });
    });

    const scroller = document.getElementById('lesson-scroller')!;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = (entry.target as HTMLElement).id;
          if (id) this.pipeline.setContentElement(id);
        }
      });
    }, { root: scroller, threshold: 0.5 });

    this.lessonContent.querySelectorAll('.lesson-section').forEach(section => {
      observer.observe(section);
    });
  }

  // -------------------------------------------------------------------------
  // Pipeline callbacks
  // -------------------------------------------------------------------------

  updateCondition(state: LearningConditionState): void {
    this.updatePulse(state);
    if (this.drawerOpen) this.updateDrawer(state);
  }

  showIntervention(intervention: Intervention): void {
    this.currentIntervention = intervention;
    const overlay = this.interventionOverlay;

    const messageEl = overlay.querySelector('.intervention-sheet__message')!;
    const optionsEl = overlay.querySelector('.intervention-sheet__options')!;
    const confidenceEl = overlay.querySelector('.intervention-sheet__confidence')!;

    messageEl.textContent = intervention.message;
    confidenceEl.textContent = `confidence: ${Math.round(intervention.confidence * 100)}%`;

    optionsEl.innerHTML = intervention.options.map(opt => {
      let cls = 'intervention-sheet__option';
      if (opt.action === 'accept') cls += ' intervention-sheet__option--primary';
      if (opt.action === 'dismiss') cls += ' intervention-sheet__option--dismiss';
      return `<button class="${cls}" data-action="${opt.action}" data-id="${opt.id}">${opt.label}</button>`;
    }).join('');

    optionsEl.querySelectorAll('.intervention-sheet__option').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const action = (e.currentTarget as HTMLElement).dataset.action as 'accept' | 'dismiss' | 'alternative';
        this.handleInterventionResponse(action);
      });
    });

    overlay.classList.add('active');
  }

  private handleInterventionResponse(action: 'accept' | 'dismiss' | 'alternative'): void {
    if (this.currentIntervention) {
      this.pipeline.recordInterventionResponse(this.currentIntervention.id, action);
      if (action === 'accept' && this.currentIntervention.class === 'reset') {
        this.showMicroBreak();
      }
    }
    this.currentIntervention = null;
    this.interventionOverlay.classList.remove('active');
  }

  updatePolicyReasoning(reasoning: string): void {
    const el = document.getElementById('status-reasoning');
    if (el) el.textContent = reasoning;
  }

  updateTier(tier: PerceptionTier): void {
    const tierEl = document.getElementById('status-tier');
    if (tierEl) {
      const labels = ['Tier 0 · telemetry', 'Tier 1 · camera', 'Tier 2 · depth'];
      tierEl.textContent = labels[tier] ?? 'unknown';
    }
  }

  updatePerceptionState(state: PerceptionState): void {
    const el = document.getElementById('status-perception');
    if (el) el.textContent = state;
  }

  updateRoomPresence(presence: RoomPresence): void {
    const el = document.getElementById('status-reasoning');
    if (!el) return;

    // Show presence info in the status bar when relevant
    if (presence.mentor_detected && !presence.pupil_detected) {
      el.textContent = 'mentor present — interventions paused';
    } else if (presence.mentor_detected && presence.pupil_detected) {
      el.textContent = 'co-learning — fewer interruptions';
    }

    // Let SAMWISE react to presence changes
    this.sam.onRoomPresenceChange(presence);
  }

  // -------------------------------------------------------------------------
  // Pulse indicator
  // -------------------------------------------------------------------------

  private updatePulse(state: LearningConditionState): void {
    const pulse = this.pulseIndicator;
    pulse.className = 'pulse-indicator';

    if (state.confidence < 0.3) {
      pulse.classList.add('pulse-indicator--off');
    } else if (state.dominant === 'attentive') {
      pulse.classList.add('pulse-indicator--steady');
    } else if (state.dominant === 'wandering' || state.dominant === 'confused') {
      pulse.classList.add('pulse-indicator--wavering');
    } else {
      pulse.classList.add('pulse-indicator--fading');
    }
  }

  // -------------------------------------------------------------------------
  // Explainability drawer
  // -------------------------------------------------------------------------

  toggleDrawer(): void {
    this.drawerOpen = !this.drawerOpen;
    this.explainabilityDrawer.classList.toggle('open', this.drawerOpen);

    if (this.drawerOpen) {
      const state = this.pipeline.getLastCondition();
      if (state) this.updateDrawer(state);
    }
  }

  private updateDrawer(state: LearningConditionState): void {
    const conditionsEl = document.getElementById('drawer-conditions')!;
    conditionsEl.innerHTML = Object.entries(state.condition)
      .map(([name, prob]) => `
        <div class="explainability-drawer__condition">
          <span class="explainability-drawer__condition-name">${name}</span>
          <div class="explainability-drawer__condition-bar">
            <div class="explainability-drawer__condition-fill" style="width: ${Math.round(prob * 100)}%"></div>
          </div>
        </div>
      `).join('');

    const confEl = document.getElementById('drawer-confidence')!;
    confEl.textContent = `${Math.round(state.confidence * 100)}%`;

    const driversEl = document.getElementById('drawer-drivers')!;
    if (state.drivers.length > 0) {
      driversEl.innerHTML = state.drivers
        .map(d => `<div class="explainability-drawer__driver">${d.description}</div>`)
        .join('');
    } else {
      driversEl.innerHTML = '<div class="explainability-drawer__driver">No strong signals detected</div>';
    }

    const notUsedEl = document.getElementById('drawer-not-used')!;
    notUsedEl.innerHTML = state.not_used
      .map(n => `<div class="explainability-drawer__not-used">${n}</div>`)
      .join('');
  }

  // -------------------------------------------------------------------------
  // Camera permission dialog
  // -------------------------------------------------------------------------

  private showCameraDialog(): void {
    this.cameraDialog.classList.add('active');
  }

  private async handleCameraAndMicAllow(): Promise<void> {
    this.cameraDialog.classList.remove('active');
    await this.pipeline.initPerception();
    await this.pipeline.initMicrophone();
    this.pipeline.start();
  }

  private async handleCameraOnlyAllow(): Promise<void> {
    this.cameraDialog.classList.remove('active');
    await this.pipeline.initPerception();
    this.pipeline.start();
  }

  private handleCameraSkip(): void {
    this.cameraDialog.classList.remove('active');
    this.pipeline.start();
  }

  // -------------------------------------------------------------------------
  // Micro-break
  // -------------------------------------------------------------------------

  private showMicroBreak(durationS = 20): void {
    this.microBreak.classList.add('active');
    const timerEl = this.microBreak.querySelector('.micro-break__timer')!;
    let remaining = durationS;

    const tick = setInterval(() => {
      remaining--;
      timerEl.textContent = `${remaining}s`;
      if (remaining <= 0) {
        clearInterval(tick);
        this.dismissMicroBreak();
      }
    }, 1000);

    timerEl.textContent = `${remaining}s`;
    (this.microBreak as any)._interval = tick;
  }

  private dismissMicroBreak(): void {
    const tick = (this.microBreak as any)._interval;
    if (tick) clearInterval(tick);
    this.microBreak.classList.remove('active');
  }

  // -------------------------------------------------------------------------
  // Privacy controls
  // -------------------------------------------------------------------------

  private async handleCameraToggle(enabled: boolean): Promise<void> {
    await this.pipeline.toggleCamera(enabled);
  }

  // -------------------------------------------------------------------------
  // Event binding
  // -------------------------------------------------------------------------

  private bindEvents(): void {
    // Privacy from home
    document.getElementById('btn-privacy')?.addEventListener('click', () => {
      this.showScreen('privacy');
    });

    // Activities picker: back → home
    document.getElementById('btn-activities-back')?.addEventListener('click', () => {
      this.showScreen('home');
    });

    // Activity player: back → activities
    document.getElementById('btn-activity-back')?.addEventListener('click', () => {
      this.showScreen('activities');
    });

    // Lesson header: back → home
    document.getElementById('btn-back')?.addEventListener('click', () => {
      this.pipeline.pause();
      this.showScreen('home');
    });

    // Pulse indicator → open drawer
    this.pulseIndicator.addEventListener('click', () => {
      this.toggleDrawer();
    });

    // Drawer close
    document.getElementById('drawer-close')?.addEventListener('click', () => {
      this.toggleDrawer();
    });

    // Camera / microphone dialog
    document.getElementById('btn-camera-allow')?.addEventListener('click', () => {
      this.handleCameraAndMicAllow();
    });

    document.getElementById('btn-camera-only')?.addEventListener('click', () => {
      this.handleCameraOnlyAllow();
    });

    document.getElementById('btn-camera-skip')?.addEventListener('click', () => {
      this.handleCameraSkip();
    });

    // Micro-break dismiss
    this.microBreak.querySelector('.micro-break__dismiss')?.addEventListener('click', () => {
      this.dismissMicroBreak();
    });

    // Privacy controls
    document.getElementById('btn-privacy-back')?.addEventListener('click', () => {
      if (this.currentScreen === 'privacy') {
        this.showScreen(this.currentLesson ? 'lesson' : 'home');
      }
    });

    document.getElementById('toggle-camera')?.addEventListener('change', (e) => {
      const checked = (e.target as HTMLInputElement).checked;
      this.handleCameraToggle(checked);
    });

    document.getElementById('toggle-microphone')?.addEventListener('change', (e) => {
      const checked = (e.target as HTMLInputElement).checked;
      this.pipeline.toggleMicrophone(checked);
    });

    // Drawer click outside to close
    this.explainabilityDrawer.addEventListener('click', (e) => {
      if (e.target === this.explainabilityDrawer) {
        this.toggleDrawer();
      }
    });

    // Intervention overlay click outside
    this.interventionOverlay.addEventListener('click', (e) => {
      if (e.target === this.interventionOverlay) {
        this.handleInterventionResponse('dismiss');
      }
    });
  }

  // -------------------------------------------------------------------------
  // SAMWISE dialogue
  // -------------------------------------------------------------------------

  private showSamDialogue(text: string): void {
    this.samText.textContent = text;
    this.samChoices.innerHTML = '';
    this.samDialogue.classList.add('active');
  }

  private showSamChoices(prompt: ConversationPrompt): void {
    this.samChoices.innerHTML = prompt.responses.map((r, i) => `
      <button class="samwise-dialogue__choice-btn" data-prompt-id="${prompt.id}" data-choice-index="${i}">
        ${r.label}
      </button>
    `).join('');

    // Bind choice clicks
    this.samChoices.querySelectorAll('.samwise-dialogue__choice-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const el = e.currentTarget as HTMLElement;
        const promptId = el.dataset.promptId!;
        const choiceIndex = parseInt(el.dataset.choiceIndex!, 10);
        this.samChoices.innerHTML = '';
        this.sam.handleChoice(promptId, choiceIndex);
      });
    });
  }

  private hideSamDialogue(): void {
    this.samDialogue.classList.remove('active');
  }

  /**
   * Forward room presence changes to SAMWISE companion.
   */
  notifySamPresence(presence: RoomPresence): void {
    this.sam.onRoomPresenceChange(presence);
  }

  // -------------------------------------------------------------------------
  // Utility
  // -------------------------------------------------------------------------

  private escapeAttr(s: string): string {
    return s.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}
